// ROUTE-SZINTŰ jog-guard: a jogosultság-ellenőrzés MÁR A NAVIGÁCIÓ ELŐTT megtörténik.
//
// MIÉRT KELL (a hiányzó réteg): eddig a route-ok csak `requireAuth()`-ot futtattak (be van-e lépve), a
// jog-ellenőrzés pedig CSAK az oldalon belül, renderelés után jelent meg egy „nincs jogosultság" dobozként.
// Következmény: a felhasználó URL-lel eljutott olyan oldalra, amit nem használhat, végigolvasta a fejlécet,
// és csak ott derült ki, hogy nincs joga. Rosszabb esetben — ha a jog-kapu réseket hagy — ki is töltött egy
// űrlapot, mielőtt a mentés visszadobta.
//
// A BEVETT MINTA három rétegű, és mind a három kell:
//   1. a BELÉPŐ (gomb/menüpont) rejtve vagy tiltva, ha nincs jog  → `ListNewButton enabled={...}`, nav-szűrés
//   2. a ROUTE elérhetetlen (URL-lel is) — EZ a fájl              → átirányítás, mielőtt bármi renderelne
//   3. a SZERVER dönt (deny-by-default)                           → BFF `@RequirePermissions` + Odin RLS/scope
// Az 1–2. réteg UX (ne fusson zsákutcába), a 3. a tényleges biztonsági fal. A kliens-oldali kapu SOHA nem
// helyettesíti a szerveroldalit — csak megelőzi a fölösleges utat.
import { redirect } from '@tanstack/react-router';

import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { queryClient } from '@/lib/query-client';

/** A `/session` válaszának minimál-alakja, amire a jog-döntés épül. */
interface SessionPermissions {
  permissions?: string[];
  isPlatformAdmin?: boolean;
  /** `internal` (munkatárs) vagy `partner` — a FELÜLET-választás alapja. */
  type?: string;
}

/**
 * A session-betöltés HÁROM kimenete. A `unavailable` KÜLÖN ág, és ez a lap legfontosabb megkülönböztetése:
 * „nincs jogod" (a szerver megmondta) ≠ „most nem tudjuk megkérdezni" (fojtás, hálózat, 5xx).
 */
type SessionLoad =
  | { kind: 'ok'; session: SessionPermissions }
  /** A munkamenetnek vége (401) — a `requireAuth` már kezelte, ide ez nem jut el, de a teljesség kedvéért. */
  | { kind: 'unauthorized' }
  /** ÁTMENETI: 429-fojtás · 502/503 · hálózati hiba. NEM jog-ítélet. */
  | { kind: 'unavailable' };

/**
 * A session lekérése a KÖZÖS cache-ből (a `usePermissions` ugyanezt a kulcsot használja `['session']`-nel),
 * hogy a guard ne indítson külön kérést, ha az adat már megvan.
 */
async function loadSession(): Promise<SessionLoad> {
  try {
    const data = await queryClient.ensureQueryData({
      queryKey: ['session'],
      queryFn: () => api.session.query({}),
    });
    const res = data as { status: number; body: unknown };
    if (res.status === 200) return { kind: 'ok', session: res.body as SessionPermissions };
    if (res.status === 401) return { kind: 'unauthorized' };
    // 403 a SAJÁT session-re szerkezetileg nem fordulhat elő (a végpont a hívó saját claimjeit adja) —
    // minden más nem-200 átmeneti zavar.
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}

/**
 * Route-guard: belépés + a megadott jogok VALAMELYIKE (OR). Jog hiányában átirányít (nem hibát dob), mert a
 * felhasználó szempontjából ez nem hiba, hanem „ide nem tartozol" — a cél-oldal a fallback, ahonnan jött volna.
 *
 * A `platform:admin` superuser-bypass ugyanaz, mint a `usePermissions`-ben — EGY szabály, két helyen ne térjen el.
 *
 * ══ FAIL-CLOSED A JOGRA, DE NEM AZ ELÉRHETŐSÉGRE ══════════════════════════════════════════════════════
 * A guard korábban MINDEN session-betöltési hibát „nincs jog"-ként kezelt, és átirányított. Élesben mérve
 * ez volt a „hirtelen kidob a rendszer" jelenség: a hónap-léptetés kimerítette a hívó Odin kérés-plafonját
 * (600/perc), a `/session` 429-et kapott, és a guard kirakta a felhasználót egy olyan lapról, amire VAN joga.
 * Ez ugyanaz a hibaosztály, amit a `refreshSession` már megoldott (`transient` ≠ `unauthorized`), és amit a
 * számla-generátor olvasó-rétegében is javítottunk.
 *
 * A szabály ezért: **jog-ítéletre fail-closed** (a szerver megmondta, hogy nincs jog → átirányítás), de
 * **átmeneti zavarra nem**. Egyszer újrapróbálunk, és ha még sem megy, ENGEDJÜK renderelni — mert:
 *   • ez a réteg UX-kapu, nem biztonsági fal (a fal a BFF `@RequirePermissions` + az Odin RLS, ami továbbra
 *     is dönt: jogtalan kérés 403-at kap, adat nem szivárog),
 *   • a lap a SAJÁT adat-hibáját mutatja „Újra" gombbal — ami igaz és javítható állapot,
 *   • egy átirányítás viszont HAMIS ítélet: azt állítja, hogy a felhasználónak nincs joga, holott van.
 */
export async function requirePermission(
  perms: string | string[],
  options: { fallback?: string } = {},
): Promise<void> {
  await requireAuth();

  const required = Array.isArray(perms) ? perms : [perms];
  let load = await loadSession();
  if (load.kind === 'unavailable') {
    // Egy rövid nekifutás: a fojtás-ablak másodperces, a hálózati zökkenő még rövidebb.
    await new Promise((resolve) => setTimeout(resolve, 1200));
    load = await loadSession();
  }
  // ÁTMENETI zavar a második próba után is → a lap renderelhet (a szerver marad a fal).
  if (load.kind !== 'ok') return;

  const session = load.session;
  const granted =
    session.isPlatformAdmin === true || required.some((p) => session.permissions?.includes(p));
  if (granted) return;

  // eslint-disable-next-line @typescript-eslint/only-throw-error -- a TanStack Router redirect-idiómája
  throw redirect({ to: options.fallback ?? '/' });
}

/**
 * FELÜLET-GUARD a partner-portálra: belsős tokennel a `/portal` alá nem lépünk be, hanem a belső
 * kezdőlapra irányítunk — **még a renderelés előtt**.
 *
 * ══ MIÉRT A ROUTE-ON, ÉS MIÉRT NEM (CSAK) A HÉJBAN ══════════════════════════════════════════════════════
 * Az átirányítás eddig a `PortalShell` egy `useEffect`-jében élt, tehát a gyerek-route MÁR RENDERELT, és a
 * lekérései KIMENTEK, mire az irányítás megtörtént. Mérve: egy belsős felhasználó a `/portal`-ra navigálva
 * NÉGY portál-kérést indított (`/portal/me`, `/tickets`, `/services`, `/messages`), és mind a négy 403 lett.
 * Az Odin percenkénti kérés-plafonja (600) mellett a fölösleges kör nem elvi kérdés.
 *
 * Ez a fájl fejléce maga mondja ki a három réteget, és a második pontosan ez: „a ROUTE elérhetetlen
 * (URL-lel is) — átirányítás, mielőtt bármi renderelne". A portál-route ezt eddig kihagyta.
 *
 * ══ AMI NEM VÁLTOZIK ════════════════════════════════════════════════════════════════════════════════════
 * Ez IRÁNYÍTÁS, nem védelem: a fal továbbra is a BFF (`@AllowPartner()` + a portál-szerep) és az Odin RLS.
 * A héj `useEffect`-je is MARAD — az a futás közben (pl. megszemélyesítés vége) bekövetkező típus-váltást
 * fogja, amit egy belépés-idejű guard fogalmilag nem láthat.
 *
 * ÁTMENETI zavarra (fojtás, 5xx) NEM irányítunk át — ugyanaz a szabály, mint a jog-guardnál: egy hamis
 * ítélet rosszabb, mint egy lap, ami a saját hibáját mutatja.
 */
export async function requirePartnerSurface(): Promise<void> {
  await requireAuth();
  let load = await loadSession();
  if (load.kind === 'unavailable') {
    await new Promise((resolve) => setTimeout(resolve, 1200));
    load = await loadSession();
  }
  if (load.kind !== 'ok') return;
  if (load.session.type !== 'internal') return;
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- a TanStack Router redirect-idiómája
  throw redirect({ to: '/' });
}
